import { useState } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";

import {
  useRemoveCriterion,
  useUpsertCriterion,
} from "@/application/use-cases/products/use-criteria.mutations";
import {
  CriterionFormBody,
  type CriterionFormValues,
} from "@/presentation/components/criteria/criterion-form-body";
import type { EvaluationCriterionRow } from "@/infrastructure/supabase/database.types";

interface EditCriterionDialogProps {
  criterion: EvaluationCriterionRow | null;
  onClose: () => void;
}

export function EditCriterionDialog({ criterion, onClose }: EditCriterionDialogProps) {
  return (
    <Dialog open={criterion !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-2xl">
        {criterion ? <Body criterion={criterion} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Body({ criterion, onClose }: { criterion: EvaluationCriterionRow; onClose: () => void }) {
  const [values, setValues] = useState<CriterionFormValues | null>(null);
  const upsert = useUpsertCriterion();
  const remove = useRemoveCriterion();

  const handleSave = () => {
    if (!values) {
      onClose();
      return;
    }
    upsert.mutate(
      { id: criterion.id, ...values },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  const handleDelete = () => {
    if (
      !window.confirm(
        `Delete criterion "${criterion.name}"? Scores for this criterion will be removed.`
      )
    ) {
      return;
    }
    remove.mutate(criterion.id, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit criterion</DialogTitle>
      </DialogHeader>
      <CriterionFormBody initial={criterion} onChange={setValues} />
      <DialogFooter className="!justify-between">
        <Button variant="ghost" onClick={handleDelete} className="text-destructive">
          <Trash2 className="size-3.5" />
          Delete
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
